import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  Button
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PercentIcon from '@mui/icons-material/Percent';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import { MdOutlineSystemUpdateAlt } from 'react-icons/md';
import Label from '../../../components/Label';
import { StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Mock KPI Data ──────────────────────────────────────────────────────
const SUMMARY_KPIS = [
  {
    label: 'Total Taxable Value',
    value: '₹ 12,50,000',
    icon: TrendingUpIcon,
    iconBg: '#eff6ff',
    iconColor: '#1d4ed8',
    border: '#1d4ed8'
  },
  {
    label: 'Total CGST',
    value: '₹ 45,000',
    icon: PercentIcon,
    iconBg: '#f0fdf4',
    iconColor: '#16a34a',
    border: '#16a34a'
  },
  {
    label: 'Total SGST',
    value: '₹ 45,000',
    icon: PercentIcon,
    iconBg: '#fefce8',
    iconColor: '#ca8a04',
    border: '#ca8a04'
  },
  {
    label: 'Total IGST',
    value: '₹ 72,000',
    icon: ReceiptIcon,
    iconBg: '#fff7ed',
    iconColor: '#ea580c',
    border: '#ea580c'
  },
  {
    label: 'Total GST Liability',
    value: '₹ 1,62,000',
    icon: AccountBalanceWalletIcon,
    iconBg: '#f0f9ff',
    iconColor: '#0891b2',
    border: '#4fbbc8',
    highlight: true
  }
];

// ── Mock Summary Rows ─────────────────────────────────────────────────
const ALL_SUMMARY_ROWS = [
  {
    period: 'Apr 2024',
    month: 4,
    taxable: 180000,
    cgst: 8100,
    sgst: 8100,
    igst: 0,
    total: 16200,
    status: 'Filed'
  },
  {
    period: 'May 2024',
    month: 5,
    taxable: 220000,
    cgst: 9900,
    sgst: 9900,
    igst: 18000,
    total: 37800,
    status: 'Filed'
  },
  {
    period: 'Jun 2024',
    month: 6,
    taxable: 195000,
    cgst: 8775,
    sgst: 8775,
    igst: 0,
    total: 17550,
    status: 'Pending'
  },
  {
    period: 'Jul 2024',
    month: 7,
    taxable: 310000,
    cgst: 13950,
    sgst: 13950,
    igst: 54000,
    total: 81900,
    status: 'Pending'
  },
  {
    period: 'Aug 2024',
    month: 8,
    taxable: 270000,
    cgst: 12150,
    sgst: 12150,
    igst: 0,
    total: 24300,
    status: 'Pending'
  },
  {
    period: 'Sep 2024',
    month: 9,
    taxable: 340000,
    cgst: 15300,
    sgst: 15300,
    igst: 27000,
    total: 57600,
    status: 'Pending'
  }
];

const MONTHS = [
  { value: '', label: 'All Months' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' }
];
const FY_OPTIONS = ['2024-25', '2023-24', '2022-23'];

const statusColor = (s) => ({ Filed: 'success', Pending: 'warning' }[s] || 'warning');

export default function GSTSummary() {
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedFY, setSelectedFY] = useState('2024-25');
  const [exportAnchor, setExportAnchor] = useState(null);

  const filtered = useMemo(() => {
    let data = [...ALL_SUMMARY_ROWS];
    if (selectedMonth !== '') data = data.filter((r) => r.month === selectedMonth);
    return data;
  }, [selectedMonth]);

  const handleExport = (type) => {
    setExportAnchor(null);
    // TODO: API call / library export
    console.log('GST Summary export as', type);
  };

  const handleDownloadInvoice = () => {
    // TODO: API call to download invoice
    console.log('Download invoices for', selectedMonth || 'all months', selectedFY);
  };

  return (
    <Box>
      {/* ── 5 KPI Cards ── */}
      <Grid container spacing={2} mb={3}>
        {SUMMARY_KPIS.map((kpi) => {
          const IconComp = kpi.icon;
          return (
            <Grid item xs={12} sm={6} md key={kpi.label}>
              <Card
                sx={{
                  p: 2.5,
                  borderLeft: `4px solid ${kpi.border}`,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    p: 1.2,
                    bgcolor: kpi.iconBg,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <IconComp sx={{ color: kpi.iconColor, fontSize: '1.4rem' }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 11, color: '#718096', fontWeight: 500, display: 'block' }}
                  >
                    {kpi.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      fontSize: 16,
                      mt: 0.2,
                      color: kpi.highlight ? '#0891b2' : '#1e293b'
                    }}
                  >
                    {kpi.value}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ── Period-wise Breakdown Table ── */}
      <Card>
        {/* ── Table toolbar with month filter + export + download ── */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          px={2}
          py={1.5}
          sx={{ borderBottom: '1px solid #eaedf1' }}
        >
          {/* LEFT — title */}
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: '#0d4689', fontSize: 13, whiteSpace: 'nowrap' }}
          >
            Period-wise GST Summary
          </Typography>

          {/* RIGHT — filters + actions */}
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            {/* Financial Year */}
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel sx={{ fontSize: 12 }}>Fin. Year</InputLabel>
              <Select
                label="Fin. Year"
                value={selectedFY}
                onChange={(e) => setSelectedFY(e.target.value)}
                sx={{ fontSize: 12 }}
              >
                {FY_OPTIONS.map((fy) => (
                  <MenuItem key={fy} value={fy} sx={{ fontSize: 13 }}>
                    FY {fy}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Month Filter */}
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: 12 }}>Month</InputLabel>
              <Select
                label="Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                sx={{ fontSize: 12 }}
              >
                {MONTHS.map((m) => (
                  <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Download Invoice button */}
            <Tooltip title="Download Invoice">
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadInvoice}
                sx={{ textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap' }}
              >
                Download Invoice
              </Button>
            </Tooltip>

            {/* Export icon — same pattern as PartnersList */}
            <Tooltip title="Export data" placement="top" arrow>
              <IconButton
                className="squareIconButton"
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                <MdOutlineSystemUpdateAlt size={16} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: { borderRadius: 1, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
              }}
            >
              <MenuItem onClick={() => handleExport('pdf')} sx={{ fontSize: 13 }}>
                <PictureAsPdfIcon sx={{ fontSize: 16, mr: 1, color: '#dc2626' }} /> Export as PDF
              </MenuItem>
              <MenuItem onClick={() => handleExport('excel')} sx={{ fontSize: 13 }}>
                <GridOnIcon sx={{ fontSize: 16, mr: 1, color: '#16a34a' }} /> Export as Excel
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        {/* ── Table ── */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                {['Period', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total GST', 'Status'].map(
                  (h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: 11, color: '#0d4689' }}>
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row, i) => (
                <StyledTableRow key={i}>
                  <StyledTableCell sx={{ fontWeight: 600 }}>{row.period}</StyledTableCell>
                  <StyledTableCell>₹ {row.taxable.toLocaleString()}</StyledTableCell>
                  <StyledTableCell>₹ {row.cgst.toLocaleString()}</StyledTableCell>
                  <StyledTableCell>₹ {row.sgst.toLocaleString()}</StyledTableCell>
                  <StyledTableCell>
                    {row.igst ? `₹ ${row.igst.toLocaleString()}` : '–'}
                  </StyledTableCell>
                  <StyledTableCell sx={{ fontWeight: 700 }}>
                    ₹ {row.total.toLocaleString()}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Label variant="ghost" color={statusColor(row.status)}>
                      {row.status}
                    </Label>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
}
